/**
 * LinkedIn Verifications Table Component
 *
 * Displays a table of LinkedIn verification records for survey creators
 * to see who has verified and their profile data.
 */

import React, { useState, useEffect } from 'react';
import {
  Linkedin,
  Users,
  CheckCircle,
  XCircle,
  AlertCircle,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  Loader2,
  Building,
  MapPin,
  Briefcase,
} from 'lucide-react';
import { getLinkedInVerifications, LinkedInVerification } from '../../utils/surveyApi';

interface LinkedInVerificationsTableProps {
  surveyId: number;
}

const LinkedInVerificationsTable: React.FC<LinkedInVerificationsTableProps> = ({ surveyId }) => {
  const [verifications, setVerifications] = useState<LinkedInVerification[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<number | null>(null);

  useEffect(() => {
    loadVerifications();
  }, [surveyId]);

  const loadVerifications = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await getLinkedInVerifications(surveyId);
      setVerifications(result.verifications);
    } catch (err: any) {
      setError(err.message || 'Failed to load verifications');
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status: string, isEligible: boolean) => {
    if (status === 'verified' && isEligible) {
      return <CheckCircle className="w-4 h-4 text-green-600" />;
    }
    if (status === 'rejected' || !isEligible) {
      return <XCircle className="w-4 h-4 text-red-600" />;
    }
    if (status === 'error') {
      return <AlertCircle className="w-4 h-4 text-amber-600" />;
    }
    return <AlertCircle className="w-4 h-4 text-gray-400" />;
  };

  const getStatusBadge = (status: string, isEligible: boolean) => {
    if (status === 'verified' && isEligible) {
      return 'bg-green-100 text-green-800 border-green-200';
    }
    if (status === 'rejected' || !isEligible) {
      return 'bg-red-100 text-red-800 border-red-200';
    }
    if (status === 'error') {
      return 'bg-amber-100 text-amber-800 border-amber-200';
    }
    return 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading verifications...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <span className="text-red-800">{error}</span>
        </div>
        <button
          onClick={loadVerifications}
          className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
        >
          Try again
        </button>
      </div>
    );
  }

  if (verifications.length === 0) {
    return (
      <div className="text-center py-8 bg-gray-50 rounded-lg">
        <Linkedin className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-600">No LinkedIn verifications yet</p>
        <p className="text-sm text-gray-500 mt-1">
          Verifications will appear here when users access the survey via the LinkedIn link
        </p>
      </div>
    );
  }

  const eligibleCount = verifications.filter((v) => v.is_eligible).length;
  const rejectedCount = verifications.filter((v) => !v.is_eligible).length;

  return (
    <div className="space-y-4">
      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-gray-50 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-gray-900">{verifications.length}</p>
          <p className="text-xs text-gray-500">Total Verifications</p>
        </div>
        <div className="bg-green-50 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-green-600">{eligibleCount}</p>
          <p className="text-xs text-green-700">Eligible</p>
        </div>
        <div className="bg-red-50 rounded-lg p-4 text-center">
          <p className="text-2xl font-bold text-red-600">{rejectedCount}</p>
          <p className="text-xs text-red-700">Not Eligible</p>
        </div>
      </div>

      {/* Verifications List */}
      <div className="border border-gray-200 rounded-lg overflow-hidden">
        {verifications.map((verification, index) => (
          <div key={verification.id} className={`${index > 0 ? 'border-t border-gray-200' : ''}`}>
            {/* Main Row */}
            <div
              className="flex items-center justify-between p-4 hover:bg-gray-50 cursor-pointer"
              onClick={() => setExpandedId(expandedId === verification.id ? null : verification.id)}
            >
              <div className="flex items-center gap-4">
                {/* Profile Picture */}
                <div className="w-10 h-10 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                  {verification.profile_picture_url ? (
                    <img
                      src={verification.profile_picture_url}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Users className="w-5 h-5 text-gray-400" />
                    </div>
                  )}
                </div>

                {/* Name & Info */}
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">
                      {verification.full_name || 'Unknown User'}
                    </span>
                    {getStatusIcon(verification.verification_status, verification.is_eligible)}
                  </div>
                  {verification.headline && (
                    <p className="text-sm text-gray-500 truncate max-w-xs">
                      {verification.headline}
                    </p>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-4">
                {/* Connections */}
                <div className="flex items-center gap-1.5">
                  <Users className="w-4 h-4 text-gray-400" />
                  <span className="font-medium text-gray-900">
                    {verification.connections_count.toLocaleString()}
                  </span>
                  <span className="text-xs text-gray-500">connections</span>
                </div>

                {/* Status Badge */}
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusBadge(
                    verification.verification_status,
                    verification.is_eligible,
                  )}`}
                >
                  {verification.is_eligible ? 'Eligible' : 'Not Eligible'}
                </span>

                {/* Expand Icon */}
                {expandedId === verification.id ? (
                  <ChevronUp className="w-4 h-4 text-gray-400" />
                ) : (
                  <ChevronDown className="w-4 h-4 text-gray-400" />
                )}
              </div>
            </div>

            {/* Expanded Details */}
            {expandedId === verification.id && (
              <div className="px-4 pb-4 bg-gray-50 border-t border-gray-100">
                <div className="grid grid-cols-2 gap-4 mt-3">
                  {/* Left Column */}
                  <div className="space-y-3">
                    {verification.company && (
                      <div className="flex items-center gap-2 text-sm">
                        <Building className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">{verification.company}</span>
                      </div>
                    )}
                    {verification.job_title && (
                      <div className="flex items-center gap-2 text-sm">
                        <Briefcase className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">{verification.job_title}</span>
                      </div>
                    )}
                    {verification.location && (
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-600">{verification.location}</span>
                      </div>
                    )}
                  </div>

                  {/* Right Column */}
                  <div className="space-y-3 text-sm">
                    <div>
                      <span className="text-gray-500">Verified:</span>{' '}
                      <span className="text-gray-900">{formatDate(verification.created_at)}</span>
                    </div>
                    {verification.session_id && (
                      <div>
                        <span className="text-gray-500">Session ID:</span>{' '}
                        <span className="text-gray-900">#{verification.session_id}</span>
                      </div>
                    )}
                    {verification.rejection_reason && (
                      <div>
                        <span className="text-gray-500">Reason:</span>{' '}
                        <span className="text-red-600">{verification.rejection_reason}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Profile Link */}
                {verification.linkedin_profile_url && (
                  <div className="mt-4">
                    <a
                      href={verification.linkedin_profile_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800"
                    >
                      <Linkedin className="w-4 h-4" />
                      View LinkedIn Profile
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default LinkedInVerificationsTable;
